/* TrackerBase Report: Tracking Target Events Report
 *
 * Angular Module: trackerbase-app.views.trackerbase-reports.tracking-target-events-report
 * State: tbReports.targetEvents
 * Exposed Controller: trackingTargetEventsReportCtrl
 * Controller in template as: trackingTargetEventsReport
 * 
 */
import angular from "angular";
import ngSanitize from "angular-sanitize";
import ngAnimate from "angular-animate";
import ngAria from "angular-aria";

import uiRouterModule from "@uirouter/angularjs";
import kendo from "@progress/kendo-ui";
import "kendo.timezones";
import kendoDirectivesModule from "../../shims/kendoDirectives";

import Assert from "tbAssert";
import TrackingTargetFactoryModule from "../../models/factories/TrackingTargetFactory";
import TbAppConfigDataServiceModule from "../../config/tb-app-config/TbAppConfigDataService";

const viewPageTitle = "TrackerBase: Tracking Target Events Report";
const viewRootElementId = "tbTrackingTargetEventsReportRootElement";

const tbTrackingTargetEventsGridEventNameTemplate = "<a ui-sref=\"trackingTarget({ targetId: {{::dataItem.targetId}} })\">{{dataItem.targetLastName}}</a>";

const mScopeSvc = new WeakMap();
const mDocumentSvc = new WeakMap();
const mHttpSvc = new WeakMap();
const mTrackingTargetEventsDataSource = new WeakMap();
const mTrackingTargetEventsReportGridOptions = new WeakMap();
const mTrackingTargetEventsReportChartOptions = new WeakMap();

class TrackingTargetEventsReportController {
    constructor($scope, $document, $http, TrackingTargetFactory, TbAppConfigDataService) {

        Assert.StrictAssert($scope != null, "Assertion Failure: Missing Scope Service Dependency");
        Assert.StrictAssert($document != null, "Assertion Failure: Missing Document Service Dependency");
        Assert.StrictAssert($http != null, "Assertion Failure: Missing Http Service Dependency");
        Assert.StrictAssert(TrackingTargetFactory != null, "Assertion Failure: Missing Tracking Target Factory Service Dependency");
        Assert.StrictAssert(TbAppConfigDataService != null, "Assertion Failure: Missing TrackerBase Application Configuration Data Service Dependency");

        mScopeSvc.set(this, $scope);
        mDocumentSvc.set(this, $document);
        mHttpSvc.set(this, $http);
        const tbTrackingTargetEventsApiUrl = `${TbAppConfigDataService.tbServerBaseUrl}/api/trackingtargets/datareports/targetevents`;


        let trackingTargetEventsDataSourceParams = {
            transport: {
                read: (options) => {

                    let httpParams = {
                        method: "GET",
                        url: tbTrackingTargetEventsApiUrl,
                        isArray: false
                    };

                    mHttpSvc.get(this)(httpParams)
                        .then(
                            (response) => {
                                let reportData = response.data.ReportData.map(
                                    (reportDataItem) => {
                                        return {
                                            target: TrackingTargetFactory.FromServerTrackingTarget(reportDataItem.Target),
                                            targetEvents: reportDataItem.TargetEvents
                                        };
                                    }
                                );
                                options.success(reportData);
                            },
                            (response) => { options.error(response); }
                        );
                }
            },
            serverPaging: false,
            schema: {
                model: {
                    id: "targetId",
                    fields: {
                        targetId: {
                            from: "target.trackingTargetId",
                            type: "number"
                        },
                        targetLastName: {
                            from: "target.lastName"
                        },
                        targetFirstName: {
                            from: "target.firstName"
                        },
                        targetName: {
                            from: "target.targetName"
                        },
                        targetEvents: {
                            from: "targetEvents",
                            type: "number"
                        }
                    }
                }
            },
            sort: {
                field: "targetEvents",
                dir: "desc"
            }
        };

        mTrackingTargetEventsDataSource.set(this,
            new kendo.data.DataSource(trackingTargetEventsDataSourceParams)
        );

        mTrackingTargetEventsReportGridOptions.set(this, {
            dataSource: mTrackingTargetEventsDataSource.get(this),
                columns: [
            {
                field: "targetLastName",
                title: "Last Name",
                template: tbTrackingTargetEventsGridEventNameTemplate
            },
            {
                field: "targetFirstName",
                title: "First Name"
            },
            {
                field: "targetEvents",
                title: "Number of Events"
            }
        ],
            autoBind: false,
            sortable: true,
            allowCopy: true
        });

        mTrackingTargetEventsReportChartOptions.set(this, {
            legend: { visible: false },
            seriesDefaults: { type: "bar" },
            series: [
                {
                    field: "targetEvents",
                    name: "Events On Calendar"
                }
            ],
            categoryAxis: {
                field: "targetName"
            },
            title: {
                visible: false
            },
            dataSource: mTrackingTargetEventsDataSource.get(this),
            pannable: {
                lock: "x"
            },
            zoomable: {
                mousewheel: {
                    lock: "x"
                },
                selection: {
                    lock: "x"
                }
            },
            tooltip: {
                visible: true,
                format: "{0} events",
                template: "#= category #: #= value #"
            }
        });

        mDocumentSvc.get(this).attr("title", viewPageTitle);

        mScopeSvc.get(this).$on("$destroy", function() {
            let reportViewRootElement = angular.element(`#${viewRootElementId}`);
            kendo.destroy(reportViewRootElement);
        });

    }

    get trackingTargetEventsReportGridOptions() {
        return mTrackingTargetEventsReportGridOptions.get(this);
    }

    get trackingTargetEventsReportChartOptions() {
        return mTrackingTargetEventsReportChartOptions.get(this);
    }
}

TrackingTargetEventsReportController.$inject = [
    "$scope",
    "$document",
    "$http",
    "TrackingTargetFactory",
    "TbAppConfigDataService"
];

TrackingTargetEventsReportController.dependencies = [
    ngSanitize,
    ngAnimate,
    ngAria,
    uiRouterModule,
    kendoDirectivesModule,
    TrackingTargetFactoryModule,
    TbAppConfigDataServiceModule
];

TrackingTargetEventsReportController.template = require("./TrackingTargetEventsReport.tpl.html");

TrackingTargetEventsReportController.controllerAs = "trackingTargetEventsReport";

export default TrackingTargetEventsReportController;



//////////////////
// WEBPACK FOOTER
// ./src/views/trackerbase-reports/TrackingTargetEventsReportController.js
// module id = cF0I
// module chunks = 1